<template>
    <div class="fileSelector">
        <div class="fileSelector_searchContainer">
            <div class="row mb_20">
                <!-- Seletor de tipo de pessoa -->
                <div class="modal-field grid_col_6 grid_col_s_12">
                    <sw-select
                        v-model="personType"
                        label="Tipo de Pessoa"
                        :options="[
                            { value: PERSON_TYPES.FISICA, label: 'Pessoa Física' },
                            { value: PERSON_TYPES.JURIDICA, label: 'Pessoa Jurídica' }
                        ]"
                    />
                </div>

                <!-- Seletor de empresa (só para pessoa jurídica) -->
                <div v-if="shouldShowCompanyField" class="modal-field grid_col_6 grid_col_s_12">
                    <sw-select
                        v-model="selectedCompany"
                        label="Empresa"
                        :options="companyOptions"
                        placeholder="Selecione a empresa"
                        search
                        search-label="Buscar empresa"
                        search-icon="search"
                    />
                </div>
            </div>

            <!-- Loading ou filtros condicionais -->
            <div v-if="isLoading" class="loading-container">
                <div class="loading-content">
                    <app-spinner />
                    <p>Carregando arquivos...</p>
                </div>
            </div>

            <template v-else-if="shouldShowFilters">
                <div class="row mb_20">
                    <!-- Pesquisa de arquivos -->
                    <div class="modal-field grid_col_12">
                        <sw-input
                            v-model="searchTerm"
                            label="Pesquisar arquivos"
                            placeholder="Digite o nome do arquivo..."
                            icon="search"
                        />
                    </div>
                </div>

                <div class="row">
                    <!-- Tipo do arquivo -->
                    <div class="modal-field grid_col_6 grid_col_s_12">
                        <sw-select
                            v-model="typeFilter"
                            label="Tipo do arquivo"
                            :options="typeOptions"
                            search
                            search-label="Buscar tipo de arquivo"
                            search-icon="search"
                        />
                    </div>

                    <!-- Filtro por categoria -->
                    <div class="modal-field grid_col_6 grid_col_s_12">
                        <sw-select
                            v-model="categoryFilter"
                            label="Categoria"
                            :options="categoryOptions"
                            search
                            search-label="Buscar categoria"
                            search-icon="search"
                        />
                    </div>
                </div>
            </template>

            <!-- Mensagem quando tipo de pessoa não selecionado -->
            <div v-else-if="!personType" class="person-type-message">
                <EmptyState
                    icon="user"
                    title="Selecione o tipo de pessoa"
                    description="Escolha entre Pessoa Física ou Jurídica para visualizar os arquivos disponíveis"
                />
            </div>
        </div>

        <!-- Lista de documentos - só exibe se não estiver carregando e tipo de pessoa estiver selecionado -->
        <div v-if="shouldShowFilters" class="fileSelector_documentList">
            <!-- Lista de documentos -->
            <div
                v-for="document in filteredDocuments"
                :key="document.id"
                class="fileSelector_documentItem"
                :class="{ selected: isDocumentSelected(document.id) }"
                @click="toggleDocument(document.id)"
            >
                <div class="fileSelector_documentCheckbox">
                    <sw-icon
                        :name="isDocumentSelected(document.id) ? 'check-circle' : 'circle'"
                        :class="{ checked: isDocumentSelected(document.id) }"
                    />
                </div>
                <div class="fileSelector_documentIcon">
                    <sw-icon :name="document.icon" />
                </div>
                <div class="fileSelector_documentInfo">
                    <h5>{{ document.name }}</h5>
                    <span>{{ document.type }} • {{ document.size }}</span>
                </div>
                <div class="fileSelector_documentStatus">
                    <span v-if="document.status === 'expiring' || document.status === 'expired'" :class="`status-${document.status}`">
                        {{ document.statusLabel }}
                    </span>
                </div>
            </div>

            <!-- Mensagem quando não encontrar documentos -->
            <EmptyState
                v-if="filteredDocuments.length === 0"
                icon="folder"
                title="Nenhum documento encontrado"
                description="Tente ajustar os filtros de busca ou verificar se existem documentos disponíveis."
            />
        </div>

        <div class="fileSelector_selectedCount">
            {{ selectedDocuments.length }} documento(s) selecionado(s)
        </div>

        <!-- Lista dos documentos selecionados -->
        <div v-if="selectedDocuments.length > 0" class="fileSelector_selectedDocuments">
            <h5>Documentos selecionados:</h5>
            <div class="fileSelector_selectedDocumentsList">
                <div
                    v-for="document in selectedDocumentsData"
                    :key="document.id"
                    class="fileSelector_selectedDocumentItem"
                >
                    <div class="fileSelector_selectedDocumentIcon">
                        <sw-icon :name="document.icon" />
                    </div>
                    <div class="fileSelector_selectedDocumentInfo">
                        <span class="fileSelector_selectedDocumentName">{{ document.name }}</span>
                        <span class="fileSelector_selectedDocumentDetails">{{ document.type }} • {{ document.size }}</span>
                    </div>
                    <sw-button
                        circle-icon
                        icon="cross"
                        hover-color="red"
                        @click="toggleDocument(document.id)"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref, computed, watch } from 'vue';
    import EmptyState from '@/components/empty-states/EmptyState.vue';
    import { PERSON_TYPES } from '@/constants';
    import { mockAvailableDocuments, mockDocumentClientOptions, mockDocumentCategoryOptions, mockFileCategories } from '@/data/mockData';

    // Props
    const props = defineProps({
        selectedDocuments: {
            type: Array,
            default: () => []
        },
        availableDocuments: {
            type: Array,
            default: () => mockAvailableDocuments
        },
        companyOptions: {
            type: Array,
            default: () => mockDocumentClientOptions
        },
        categoryOptions: {
            type: Array,
            default: () => [
                { value: '', label: 'Todas as categorias' },
                ...mockDocumentCategoryOptions
            ]
        }
    });

    // Emits
    const emit = defineEmits(['update:selectedDocuments']);

    // Estados internos
    const personType = ref('');
    const selectedCompany = ref('');
    const searchTerm = ref('');
    const typeFilter = ref('');
    const categoryFilter = ref('');
    const isLoading = ref(false);

    // Computed para controlar exibição dos componentes
    const shouldShowCompanyField = computed(() => {
        return personType.value === PERSON_TYPES.JURIDICA;
    });

    const shouldShowFilters = computed(() => {
        return personType.value !== '' && !isLoading.value;
    });

    // Opções para filtro de tipo de arquivo
    const typeOptions = computed(() => {
        let categories = [];
        if (personType.value === PERSON_TYPES.FISICA) {
            categories = mockFileCategories.NATURAL;
        } else if (personType.value === PERSON_TYPES.JURIDICA) {
            categories = mockFileCategories.COMPANY;
        }

        // Criar array com "Todos os tipos" no início
        const allTypesOption = {
            value: '',
            label: 'Todos os tipos',
            selected: typeFilter.value === ''
        };

        // Mapear as categorias com suboptions
        const categoryOptions = categories.map(option => {
            return {
                ...option,
                selected: option.value === typeFilter.value,
                suboptions: option.suboptions?.map(sub => ({
                    ...sub,
                    selected: sub.value === typeFilter.value
                }))
            };
        });

        // Retornar "Todos os tipos" primeiro, depois as categorias
        return [allTypesOption, ...categoryOptions];
    });

    // Documentos filtrados
    const filteredDocuments = computed(() => {
        let filtered = props.availableDocuments;

        // Filtro por busca
        if (searchTerm.value) {
            const search = searchTerm.value.toLowerCase();
            filtered = filtered.filter(doc =>
                doc.name.toLowerCase().includes(search) ||
                doc.type.toLowerCase().includes(search)
            );
        }

        // Filtro por tipo de arquivo
        if (typeFilter.value) {
            filtered = filtered.filter(doc => doc.type === typeFilter.value);
        }

        // Filtro por categoria
        if (categoryFilter.value) {
            filtered = filtered.filter(doc => doc.category === categoryFilter.value);
        }

        // Filtro por empresa selecionada (campo principal)
        if (selectedCompany.value) {
            filtered = filtered.filter(doc => doc.client === selectedCompany.value);
        }

        return filtered;
    });

    // Documentos selecionados com dados completos
    const selectedDocumentsData = computed(() => {
        return props.availableDocuments.filter(doc =>
            props.selectedDocuments.includes(doc.id)
        );
    });

    // Funções
    const isDocumentSelected = (documentId) => {
        return props.selectedDocuments.includes(documentId);
    };

    const toggleDocument = (documentId) => {
        const currentSelected = [...props.selectedDocuments];
        const index = currentSelected.indexOf(documentId);

        if (index === -1) {
            currentSelected.push(documentId);
        } else {
            currentSelected.splice(index, 1);
        }

        emit('update:selectedDocuments', currentSelected);
    };

    // Watcher para simular loading quando alterar tipo de pessoa
    watch(personType, async (newValue, oldValue) => {
        if (newValue && newValue !== oldValue) {
            // Limpar filtros anteriores
            selectedCompany.value = '';
            typeFilter.value = '';
            categoryFilter.value = '';
            searchTerm.value = '';

            // Simular loading da API
            isLoading.value = true;

            // Delay simulado entre 800ms e 1500ms
            const delay = Math.floor(Math.random() * (1500 - 800 + 1)) + 800;
            await new Promise(resolve => setTimeout(resolve, delay));

            isLoading.value = false;

            console.log(`Documentos carregados para tipo de pessoa: ${newValue}`);
        }
    });
</script>

<style lang="scss" scoped>
@import '@/assets/css/mediaScreen.scss';

.fileSelector {
    display: flex;
    flex-direction: column;
    gap: 1.5em;
}

// Loading container
.loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 3em 1em;
    background: var(--backgroundInsideBox, #f8fafc);
    border: 1px solid var(--borderColorLight, #e5e7eb);
    border-radius: 0.75em;
}

.loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1em;

    p {
        margin: 0;
        color: var(--textSecondary, #64748b);
        font-size: 0.875em;
    }
}

.person-type-message {
    padding: 2em 1em;
}

// Lista de documentos
.fileSelector_documentList {
    display: flex;
    flex-direction: column;
    gap: 0.75em;
    margin-top: 1.5em;
    max-height: 400px;
    overflow-y: auto;
    padding: 0.5em;
    border: 1px solid var(--borderColorLight, #e5e7eb);
    border-radius: 0.75em;
    background: var(--backgroundInsideBox, #f8fafc);
}

.fileSelector_documentItem {
    display: flex;
    align-items: center;
    gap: 1em;
    padding: 1em;
    border: 1px solid var(--borderColorLight, #e5e7eb);
    border-radius: 0.75em;
    cursor: pointer;
    transition: all 0.2s ease;
    background: white;

    &:hover {
        border-color: var(--colorBlue, #3b82f6);
        box-shadow: 0 2px 4px rgba(59, 130, 246, 0.1);
    }

    &.selected {
        border-color: var(--colorBlue, #3b82f6);
        background: rgba(59, 130, 246, 0.05);
    }
}

.fileSelector_documentCheckbox {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.5em;
    height: 1.5em;

    sw-icon {
        width: 1.25em;
        height: 1.25em;
        color: var(--borderColor, #d1d5db);
        transition: color 0.2s ease;

        &.checked {
            color: var(--colorBlue, #3b82f6);
        }
    }
}

.fileSelector_documentIcon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5em;
    height: 2.5em;
    background: var(--backgroundInsideBox, #f8fafc);
    border-radius: 0.5em;
    color: var(--textSecondary, #64748b);

    sw-icon {
        width: 1.25em;
        height: 1.25em;
    }
}

.fileSelector_documentInfo {
    flex: 1;
    min-width: 0;

    h5 {
        margin: 0 0 0.25em 0;
        font-size: 0.875em;
        font-weight: 600;
        color: var(--textPrimary, #1e293b);
        line-height: 1.2;
    }

    span {
        font-size: 0.75em;
        color: var(--textSecondary, #64748b);
        line-height: 1.3;
    }
}

.fileSelector_documentStatus {
    span {
        padding: 0.25em 0.75em;
        border-radius: 1em;
        font-size: 0.75em;
        font-weight: 500;

        &.status-expiring {
            background: rgba(245, 158, 11, 0.1);
            color: var(--colorOrange, #f59e0b);
        }

        &.status-expired {
            background: rgba(239, 68, 68, 0.1);
            color: var(--colorRed, #ef4444);
        }
    }
}

// Contador de selecionados
.fileSelector_selectedCount {
    text-align: center;
    font-size: 0.875em;
    color: var(--textSecondary, #64748b);
    padding: 0.75em;
    background: white;
    border: 1px solid var(--borderColorLight, #e5e7eb);
    border-radius: 0.5em;
}

// Lista de documentos selecionados
.fileSelector_selectedDocuments {
    margin-top: 1.5em;
    border: 1px solid var(--borderColorLight, #e5e7eb);
    border-radius: 0.75em;
    background: var(--backgroundInsideBox, #f8fafc);

    h5 {
        margin: 0;
        padding: 1em 1.25em 0.5em;
        font-size: 0.875em;
        font-weight: 600;
        color: var(--textPrimary, #1e293b);
        text-transform: uppercase;
        letter-spacing: 0.025em;
        border-bottom: 1px solid var(--borderColorLight, #e5e7eb);
    }
}

.fileSelector_selectedDocumentsList {
    padding: 0.75em;
    display: flex;
    flex-direction: column;
    gap: 0.5em;
}

.fileSelector_selectedDocumentItem {
    display: flex;
    align-items: center;
    gap: 0.75em;
    padding: 0.75em;
    background: white;
    border: 1px solid var(--borderColorLight, #e5e7eb);
    border-radius: 0.5em;
    transition: all 0.2s ease;

    &:hover {
        border-color: var(--colorBlue, #3b82f6);
        box-shadow: 0 2px 4px rgba(59, 130, 246, 0.1);
    }
}

.fileSelector_selectedDocumentIcon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2em;
    height: 2em;
    background: var(--backgroundInsideBox, #f8fafc);
    border-radius: 0.5em;
    color: var(--textSecondary, #64748b);

    sw-icon {
        width: 1em;
        height: 1em;
    }
}

.fileSelector_selectedDocumentInfo {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25em;
}

.fileSelector_selectedDocumentName {
    font-size: 0.875em;
    font-weight: 600;
    color: var(--textPrimary, #1e293b);
    line-height: 1.2;
}

.fileSelector_selectedDocumentDetails {
    font-size: 0.75em;
    color: var(--textSecondary, #64748b);
    line-height: 1.3;
}

// Responsividade
@include screen_medias(s) {
    .fileSelector_documentItem {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.75em;
    }

    .fileSelector_documentInfo {
        width: 100%;
    }

    .fileSelector_selectedDocumentItem {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.5em;
    }
}
</style>